import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useReducer,
	useRef,
} from "react";
import { toast } from "sonner";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import type { LoginResponse, User } from "@/types/auth";

const STORAGE_KEYS = {
	TOKEN: "token",
	USER: "user",
	LOGIN_TIMESTAMP: "loginTimestamp",
} as const;

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minut
const ABS_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 godziny

type AuthState = {
	token: string | null;
	user: User | null;
	restoring: boolean;
};

type AuthAction =
	| { type: "RESTORE_SESSION" }
	| { type: "SIGN_IN"; token: string; user: User }
	| { type: "SIGN_OUT" }
	| { type: "UPDATE_USER"; patch: Partial<User> };

type AuthContextType = {
	state: AuthState;
	isAuthenticated: boolean;
	user: User | null;
	login: (data: LoginResponse, username: string) => void;
	logout: () => void;
	updateUser: (patch: Partial<User>) => void;
};

const DEFAULT_AUTH_STATE: AuthState = {
	token: null,
	user: null,
	restoring: true,
};

export const authReducer = (
	state: AuthState,
	action: AuthAction,
): AuthState => {
	switch (action.type) {
		case "RESTORE_SESSION":
			return { ...state, restoring: true };
		case "SIGN_IN":
			return {
				token: action.token,
				user: action.user,
				restoring: false,
			};
		case "SIGN_OUT":
			return { token: null, user: null, restoring: false };
		case "UPDATE_USER":
			if (!state.user) return state;
			return { ...state, user: { ...state.user, ...action.patch } };
		default:
			return state;
	}
};

const AuthContext = createContext<AuthContextType | null>(null);

function getUserFromStorage(): User | null {
	const userJson = localStorage.getItem(STORAGE_KEYS.USER);
	if (userJson) {
		try {
			return JSON.parse(userJson) as User;
		} catch {
			return null;
		}
	}
	return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();
	const [state, dispatch] = useReducer(authReducer, DEFAULT_AUTH_STATE);

	const loginTimestampRef = useRef<number | null>(null);
	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const absTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const login = (data: LoginResponse, username: string) => {
		const { token, ...rest } = data;

		if (!token) {
			throw new Error("Session initialization failed - No authorization token");
		}

		const userData: User = { ...rest, username };

		const loginTimestamp = Date.now();
		localStorage.setItem(STORAGE_KEYS.TOKEN, token);
		localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
		localStorage.setItem(STORAGE_KEYS.LOGIN_TIMESTAMP, String(loginTimestamp));

		loginTimestampRef.current = loginTimestamp;
		dispatch({ type: "SIGN_IN", token, user: userData });
	};

	const updateUser = useCallback((patch: Partial<User>) => {
		dispatch({ type: "UPDATE_USER", patch });
	}, []);

	const logout = useCallback(() => {
		if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		if (absTimerRef.current) clearInterval(absTimerRef.current);
		idleTimerRef.current = null;
		absTimerRef.current = null;

		queryClient.cancelQueries();
		queryClient.clear();

		localStorage.removeItem(STORAGE_KEYS.TOKEN);
		localStorage.removeItem(STORAGE_KEYS.USER);
		localStorage.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);

		loginTimestampRef.current = null;

		dispatch({ type: "SIGN_OUT" });
	}, [queryClient]);

	useEffect(() => {
		if (state.user) {
			localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(state.user));
		}
	}, [state.user]);

	useEffect(() => {
		dispatch({ type: "RESTORE_SESSION" });

		const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
		const storedUser = getUserFromStorage();
		const storedTimestamp = localStorage.getItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
		const loginTimestamp = storedTimestamp ? Number(storedTimestamp) : NaN;
		const isAbsExpired =
			!Number.isFinite(loginTimestamp) ||
			Date.now() - loginTimestamp >= ABS_TIMEOUT_MS;

		if (token && storedUser && !isAbsExpired) {
			loginTimestampRef.current = loginTimestamp;
			dispatch({ type: "SIGN_IN", token, user: storedUser });
		} else {
			localStorage.removeItem(STORAGE_KEYS.TOKEN);
			localStorage.removeItem(STORAGE_KEYS.USER);
			localStorage.removeItem(STORAGE_KEYS.LOGIN_TIMESTAMP);
			dispatch({ type: "SIGN_OUT" });
		}
	}, []);

	const isAuthenticated = !!state.token && !state.restoring;

	useEffect(() => {
		if (!isAuthenticated) return;

		function resetIdleTimer() {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
			idleTimerRef.current = setTimeout(() => {
				toast.info(i18n.t("auth.sessionIdleExpired"));
				logout();
			}, IDLE_TIMEOUT_MS);
		}

		const events = ["mousemove", "keydown", "click", "touchstart"] as const;
		events.forEach((e) => window.addEventListener(e, resetIdleTimer));
		resetIdleTimer();

		if (!loginTimestampRef.current) {
			loginTimestampRef.current = Date.now();
		}
		absTimerRef.current = setInterval(() => {
			if (
				loginTimestampRef.current !== null &&
				Date.now() - loginTimestampRef.current >= ABS_TIMEOUT_MS
			) {
				toast.info(i18n.t("auth.sessionExpired"));
				logout();
			}
		}, 60 * 1000);

		return () => {
			events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
			if (absTimerRef.current) clearInterval(absTimerRef.current);
		};
	}, [isAuthenticated, logout]);

	useLayoutEffect(() => {
		const requestInterceptor = api.interceptors.request.use((config) => {
			const skipAuthUrls = ["/api/auth/login/", "/api/auth/register/"];
			if (skipAuthUrls.some((url) => config.url?.includes(url))) {
				return config;
			}

			const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
			if (token) {
				config.headers.Authorization = `Token ${token}`;
			}
			return config;
		});

		return () => api.interceptors.request.eject(requestInterceptor);
	}, []);

	useLayoutEffect(() => {
		const responseInterceptor = api.interceptors.response.use(
			(response) => response,
			(error) => {
				const status = error?.response?.status;
				if (status === 401 && state.token) {
					logout();
				}
				return Promise.reject(error);
			},
		);

		return () => api.interceptors.response.eject(responseInterceptor);
	}, [state.token, logout]);

	return (
		<AuthContext.Provider
			value={{
				state,
				isAuthenticated,
				user: state.user,
				login,
				logout,
				updateUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
